import React, { useEffect, useMemo, useRef, useState } from "react";
import { wormColors } from "core/src/core/constants.js";
import DynamicLayer from "../play/dynamic/DynamicLayer";
import { Opening } from "../play/dynamic/RoyaleMoments";
import type { DynamicLayerProps, PublicPlayer } from "../play/dynamic/types";
import type { NextRound, RoundResult, RoundSetup, RoyaleResult } from "../../online/dynamic";

// /debug/royale-flow: every screen the Royale lays over the game, played with
// made-up players by the game's own components (DynamicLayer, the opening), on
// the game's own clock. Pick a phase on the left; it plays in the frame, which
// is a real viewport (an iframe), so the phone layout is the phone layout.
// Nothing here is a copy: a change to the Royale's screens shows here as is.
// The numbers below mirror edge-server/src/directors/royale.js.
const FIELDS = [30, 23, 17, 12, 8, 5, 3, 2];
const CUTS = [7, 6, 5, 4, 3, 2, 1];
const STAGE_ROUNDS = 3, FINAL_WINS = 3, NUMBER = 12;
const ROUND_PAUSE_MS = 9000, STAGE_PAUSE_MS = 16000, FINAL_PAUSE_MS = 11000, OPENING_MS = 6500;
const START_PHASE_S = 8; // the game's own start of a round: the players presented, then 3, 2, 1

const NAMES = ["LuckyLuke", "ColdNoodle", "MisoHungry", "GummyWorm", "LeadFoot", "SkidMark", "SpicyRamen", "KnotBad", "TokyoTurn", "PadThaiGuy", "BoxedIn", "LoopDeLou", "WasabiWorm", "LastLaugh", "NearMiss", "DriftDaisy", "GapSnipe", "TwirlGirl", "SushiRoll", "CurveBall", "ZigZagZoe", "WallHugger", "TightTurn", "SlowPoke", "HotLap", "SideSwipe", "DeadEnd", "OneMoreGo", "LateBrake", "FullSend"];
const FLAGS = ["se", "dk", "it", "de", "es", "gb", "ro", "fr", "hu", "au", "fi", "at", "pl", "us", "nl", "br", "ar", "ch", "pt", "ca", "jp", "ua", "gr", "mx", "no", "tr", "cz", "kr", "ie", "be"];
const COLOR_IDS = Object.keys(wormColors as Record<string, string>);
const P: PublicPlayer[] = NAMES.map((name, i) => ({ id: "p" + i, name, flag: FLAGS[i], colorId: COLOR_IDS[i % COLOR_IDS.length] }));
const BY_ID: Record<string, PublicPlayer> = Object.fromEntries(P.map((p) => [p.id, p]));
// The lower a player's number the further they get: p0 wins the final against p1, p2 is third.
const WINNER = "p0", SECOND = "p1";

// Whose screen it is.
type As = "winner" | "second" | "third" | "out" | "spectator";
const AS: { id: As; label: string }[] = [
  { id: "winner", label: "The winner" }, { id: "second", label: "Second (loses the final)" }, { id: "third", label: "Third (out at 3 left)" },
  { id: "out", label: "Out in stage 1" }, { id: "spectator", label: "Watching, never played" },
];
const SEAT: Record<As, string | null> = { winner: "p0", second: "p1", third: "p2", out: "p25", spectator: null };

const sizeKeyFor = (n: number) => (n > 17 ? "huge" : n > 8 ? "large" : n > 4 ? "medium" : "small");
const MODES = ["Classic", "Sabotage", "Fatboy Slim", "Identity crisis", "Drink and drive"];
function setupFor(stage: number, round: number, mode = MODES[(stage + round) % MODES.length]): RoundSetup {
  const final = stage >= CUTS.length;
  const alive = FIELDS[Math.min(stage, FIELDS.length - 1)];
  return {
    dynamic: "royale", mode, mapDesc: null, sizeKey: sizeKeyFor(alive) as any, shape: "Stadium" as any, round: stage * STAGE_ROUNDS + round, scoreReset: round === 1,
    number: NUMBER, alive, stageNo: stage + 1, stages: CUTS.length, stageRound: round, stageRounds: STAGE_ROUNDS, out: final ? 0 : CUTS[stage], cut: !final && round === STAGE_ROUNDS,
    fields: FIELDS, final, finalWins: FINAL_WINS, size: 30, royaleRound: stage * STAGE_ROUNDS + round,
  } as RoundSetup;
}
// A stage's table after `round` rounds: neighbours level on points in pairs, so
// the cut line has players level across it.
const stageScore = (n: number, round: number): Record<string, number> =>
  Object.fromEntries(P.slice(0, n).map((p, k) => [p.id, Math.floor((n - k + 1) / 2) * 2 * round]));
const places = (ids: string[]) => Object.fromEntries(ids.map((id) => [id, Number(id.slice(1)) + 1]));

// A step of a scene: what the layer is given, for how long (the last one stays).
interface Step { ms?: number; phase: "playing" | "countdown" | "over"; setup: RoundSetup; next?: NextRound; result?: RoundResult; score: Record<string, number>; roundWinnerId?: string; royaleResult?: RoyaleResult; opening?: boolean; roster?: number }
const playing = (stage: number, round: number, ms?: number, score?: Record<string, number>): Step =>
  ({ ms, phase: "playing", setup: setupFor(stage, round), score: score || (stage >= CUTS.length ? { p0: 0, p1: 0 } : stageScore(FIELDS[stage], round - 1)) });
// The pause after `round` of `stage`.
function pause(stage: number, round: number): Step {
  const n = FIELDS[stage], last = round === STAGE_ROUNDS;
  const out = last ? P.slice(FIELDS[stage + 1], n).map((p) => p.id).reverse() : [];
  const toFinal = last && stage + 1 >= CUTS.length;
  return {
    ms: toFinal ? FINAL_PAUSE_MS : last ? STAGE_PAUSE_MS : ROUND_PAUSE_MS, phase: "countdown", setup: setupFor(stage, round),
    next: (last ? setupFor(stage + 1, 1) : setupFor(stage, round + 1)) as NextRound,
    result: { royale: true, stageOver: last, out, places: places(out), alive: last ? FIELDS[stage + 1] : n },
    score: stageScore(n, round), roundWinnerId: "p1",
  };
}
function finalPause(a: number, b: number, won: string): Step {
  return { ms: ROUND_PAUSE_MS, phase: "countdown", setup: setupFor(7, a + b), next: setupFor(7, a + b + 1) as NextRound, result: { royale: true, alive: 2 }, score: { p0: a, p1: b }, roundWinnerId: won };
}
const finalPlay = (a: number, b: number, ms?: number): Step => playing(7, a + b + 1, ms, { p0: a, p1: b });
const ended: Step = {
  phase: "over", setup: setupFor(7, 5), score: { p0: 3, p1: 2 }, roundWinnerId: WINNER,
  result: { royale: true, winner: WINNER, alive: 1, places: { p0: 1, p1: 2 } },
  royaleResult: { number: NUMBER, winner: WINNER, placements: P.map((p) => p.id), size: 30 },
};

interface Scene { id: string; group: string; label: string; steps: Step[] }
const SCENES: Scene[] = [
  { id: "opening", group: "Start", label: "The opening, into round 1", steps: [{ ...playing(0, 1), opening: true }] },
  { id: "play", group: "A stage", label: "A round being played", steps: [playing(0, 1)] },
  { id: "pause1", group: "A stage", label: "After round 1 (30 players)", steps: [pause(0, 1), playing(0, 2)] },
  { id: "pause2", group: "A stage", label: "After round 2: Knockout round next", steps: [pause(0, 2), playing(0, 3)] },
  { id: "ko", group: "A stage", label: "The Knockout round being played", steps: [playing(0, 3)] },
  { id: "stage1", group: "A stage ends", label: "30 to 23: cut line, wall, next round", steps: [pause(0, 3), playing(1, 1)] },
  { id: "stage5", group: "A stage ends", label: "8 to 5", steps: [pause(4, 3), playing(5, 1)] },
  { id: "stage6", group: "A stage ends", label: "5 to 3", steps: [pause(5, 3), playing(6, 1)] },
  { id: "three2", group: "3 players left", label: "After round 2 with 3 left", steps: [pause(6, 2), playing(6, 3)] },
  { id: "toFinal", group: "3 players left", label: "3 to 2: one out, into the final", steps: [pause(6, 3), finalPlay(0, 0)] },
  { id: "final", group: "The final", label: "The final being played", steps: [finalPlay(0, 0)] },
  { id: "final10", group: "The final", label: "Between rounds, 1:0", steps: [finalPause(1, 0, "p0"), finalPlay(1, 0)] },
  { id: "final22", group: "The final", label: "Between rounds, 2:2", steps: [finalPause(2, 2, "p1"), finalPlay(2, 2)] },
  { id: "end", group: "The end", label: "The Royale is decided", steps: [ended] },
  {
    id: "run", group: "Play through", label: "From 3 players to the winner",
    steps: [playing(6, 3, 5000), pause(6, 3), finalPlay(0, 0, 14000), finalPause(1, 0, "p0"), finalPlay(1, 0, 14000), finalPause(1, 1, "p1"), finalPlay(1, 1, 14000), finalPause(2, 1, "p0"), finalPlay(2, 1, 14000), ended],
  },
  { id: "all", group: "Play through", label: "Stage 1 from the opening to the cut", steps: [{ ...playing(0, 1, OPENING_MS + 14000), opening: true }, pause(0, 1), playing(0, 2, 14000), pause(0, 2), playing(0, 3, 14000), pause(0, 3), playing(1, 1)] },
];

// ---------- the frame's inside: a stand-in board with the real layer over it ----------
function Board({ step, since }: { step: Step; since: number }) {
  const [, tick] = useState(0);
  useEffect(() => { const id = window.setInterval(() => tick((n) => n + 1), 100); return () => window.clearInterval(id); }, []);
  const n = step.setup.alive || 2;
  // the game's own start of a round, as a note: this page has no game under the layer
  const t = (Date.now() - since) / 1000 - (step.opening ? OPENING_MS / 1000 : 0);
  const left = START_PHASE_S - t;
  const note = step.phase !== "playing" ? (step.phase === "countdown" ? "The round is over (the winner's lap)" : "") : t < 0 ? "" : left > 3 ? "The game presents the players" : left > 0 ? String(Math.ceil(left)) : "The round is played";
  return (
    <div style={{ position: "fixed", inset: 0, background: "radial-gradient(90% 90% at 50% 40%, #101a30, #060a14)" }}>
      <svg viewBox="0 0 800 400" preserveAspectRatio="none" style={{ position: "absolute", inset: "60px 24px 24px", width: "calc(100% - 48px)", height: "calc(100% - 84px)", border: "2px solid rgba(233,243,255,0.25)", borderRadius: 18, boxSizing: "border-box" }} aria-hidden="true">
        {P.slice(0, Math.min(n, 16)).map((p, i) => <path key={p.id} d={`M${40 + (i * 47) % 700} ${40 + (i * 83) % 300} q ${40 + (i % 3) * 20} ${-30 + (i % 5) * 16}, 90 ${10 + (i % 4) * 12} t 70 ${i % 2 ? 30 : -24}`} fill="none" stroke={(wormColors as any)[p.colorId]} strokeWidth="4" strokeLinecap="round" opacity="0.55" />)}
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 34, textAlign: "center", font: `700 ${/^\d$/.test(note) ? 64 : 13}px "Chakra Petch", sans-serif`, letterSpacing: 1, color: "rgba(233,243,255,0.4)" }}>{note}</div>
    </div>
  );
}

function Stage({ scene, as }: { scene: Scene; as: As }) {
  const [at, setAt] = useState(() => ({ i: 0, since: Date.now() }));
  const step = scene.steps[at.i];
  useEffect(() => {
    if (step.ms == null || at.i >= scene.steps.length - 1) return;
    const id = window.setTimeout(() => setAt({ i: at.i + 1, since: Date.now() }), Math.max(0, at.since + step.ms - Date.now()));
    return () => window.clearTimeout(id);
  }, [at, step, scene]);
  // when the pause began (the layer keeps it while the next round plays)
  const pauseRef = useRef({ at: 0, ms: 0 });
  if (step.phase === "countdown") pauseRef.current = { at: at.since, ms: step.ms || ROUND_PAUSE_MS };
  const roundRef = useRef(at.since);
  if (step.phase === "playing") roundRef.current = at.since + (step.opening ? OPENING_MS : 0);

  const seat = SEAT[as];
  const players = useMemo(() => P.slice(0, step.setup.alive || 2), [step.setup.alive]);
  const seatIds = seat ? [seat] : [];
  const inRound = !!seat && players.some((p) => p.id === seat);
  const gs = useRef<any>({ gameEvents: [], startPhaseTime: START_PHASE_S, gameTime: 0 });
  const getGameState = useRef(() => { gs.current.gameTime = Math.max(0, (Date.now() - roundRef.current) / 1000); return gs.current; }).current;
  const props: DynamicLayerProps = {
    kind: "royale", phase: step.phase, setup: step.setup, next: step.phase === "countdown" ? step.next || null : null,
    result: step.phase !== "playing" ? step.result || null : null, royaleResult: step.royaleResult || null,
    players, playerById: BY_ID, score: step.score, roundWinnerId: step.phase === "playing" ? null : step.roundWinnerId || null,
    seatIds, youIds: inRound ? seatIds : [], pending: false,
    myPlace: seat && !inRound ? Number(seat.slice(1)) + 1 : step.phase === "over" && seat ? Number(seat.slice(1)) + 1 : null,
    pauseStartedAt: pauseRef.current.at, pauseMs: pauseRef.current.ms, moved: false, touch: false, getGameState,
    onLeave: () => {}, onAgain: () => {}, onPlayNow: () => {},
  };
  return (
    <>
      <Board step={step} since={at.since} />
      {step.opening && <Opening players={players} mine={seatIds} at={at.since} ms={OPENING_MS} rounds={STAGE_ROUNDS} out={step.setup.out || 0} />}
      {!(step.opening && Date.now() < at.since + OPENING_MS) && <DynamicLayer {...props} />}
      <OpeningEnd on={!!step.opening} at={at.since} />
    </>
  );
}
// (re-renders the stage when the opening's hold ends, as the game does when round 1 starts)
function OpeningEnd({ on, at }: { on: boolean; at: number }) {
  const [, tick] = useState(0);
  useEffect(() => { if (!on) return; const id = window.setTimeout(() => tick(1), Math.max(0, at + OPENING_MS - Date.now()) + 30); return () => window.clearTimeout(id); }, [on, at]);
  return null;
}

function Inside() {
  const [show, setShow] = useState<{ scene: string; as: As; k: number } | null>(null);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => { if (e.origin === window.location.origin && e.data && e.data.royaleFlow) setShow(e.data.royaleFlow); };
    window.addEventListener("message", onMsg);
    window.parent.postMessage({ royaleFlowReady: true }, window.location.origin);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  const scene = show && SCENES.find((x) => x.id === show.scene);
  // a parent whose key changes: the whole stage is made afresh on every replay
  return scene && show ? <Stage key={show.k + scene.id + show.as} scene={scene} as={show.as} /> : null;
}

// ---------- the page: the list of phases, and the frame ----------
const DEVICES = [{ id: "pc", label: "PC", w: 0, h: 0 }, { id: "phone", label: "Phone 844 x 390", w: 844, h: 390 }, { id: "small", label: "Small phone 667 x 375", w: 667, h: 375 }];
export default function RoyaleFlowSheet() {
  if (new URLSearchParams(window.location.search).get("stage") === "1") return <Inside />;
  return <Outside />;
}
function Outside() {
  const [scene, setScene] = useState(() => new URLSearchParams(window.location.search).get("s") || "toFinal");
  const [as, setAs] = useState<As>(() => (new URLSearchParams(window.location.search).get("as") as As) || "winner");
  const [device, setDevice] = useState("pc");
  const [k, setK] = useState(0);
  // the final's versus screen: which design (the layer reads it from storage)
  const [vs, setVs] = useState(() => { try { return Number(window.localStorage.getItem("royaleVs")) || 1; } catch { return 1; } });
  const pickVs = (n: number) => { try { window.localStorage.setItem("royaleVs", String(n)); } catch { /* no storage */ } setVs(n); setScene("toFinal"); setK((v) => v + 1); };
  const [started, setStarted] = useState(Date.now());
  const frame = useRef<HTMLIFrameElement>(null);
  const send = () => { frame.current?.contentWindow?.postMessage({ royaleFlow: { scene, as, k } }, window.location.origin); setStarted(Date.now()); };
  useEffect(() => { send(); window.history.replaceState(null, "", `?s=${scene}&as=${as}`); }, [scene, as, k, device]);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => { if (e.data && e.data.royaleFlowReady) send(); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  });
  const [, tick] = useState(0);
  useEffect(() => { const id = window.setInterval(() => tick((n) => n + 1), 100); return () => window.clearInterval(id); }, []);
  const dev = DEVICES.find((d) => d.id === device)!;
  const groups = SCENES.reduce<string[]>((a, x) => (a.indexOf(x.group) === -1 ? [...a, x.group] : a), []);
  const btn = (on: boolean): React.CSSProperties => ({ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", marginBottom: 3, borderRadius: 6, border: "1px solid " + (on ? "#ffd23f" : "rgba(255,255,255,0.1)"), background: on ? "rgba(255,210,63,0.14)" : "transparent", color: on ? "#ffd23f" : "#e9f3ff", font: "500 13px Outfit, system-ui, sans-serif", cursor: "pointer" });
  const head: React.CSSProperties = { margin: "14px 0 6px", font: "700 11px 'Chakra Petch', sans-serif", letterSpacing: 1.6, textTransform: "uppercase", color: "#8ea3bd" };
  return (
    <div style={{ position: "fixed", inset: 0, display: "flex", background: "#04070e", color: "#e9f3ff", font: "500 13px Outfit, system-ui, sans-serif" }}>
      <div style={{ flex: "0 0 270px", overflowY: "auto", padding: "14px 14px 30px", borderRight: "1px solid rgba(255,255,255,0.1)", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" onClick={() => setK((v) => v + 1)} style={{ ...btn(true), width: "auto", margin: 0, fontWeight: 700 }}>Replay</button>
          <b style={{ font: "700 18px 'Chakra Petch', sans-serif", fontVariantNumeric: "tabular-nums" }}>{((Date.now() - started) / 1000).toFixed(1)} s</b>
        </div>
        <div style={head}>Whose screen</div>
        {AS.map((a) => <button type="button" key={a.id} style={btn(as === a.id)} onClick={() => setAs(a.id)}>{a.label}</button>)}
        <div style={head}>Versus screen design</div>
        <div style={{ display: "flex", gap: 4 }}>{[1, 2, 3, 4, 5].map((n) => <button type="button" key={n} style={{ ...btn(vs === n), textAlign: "center" }} onClick={() => pickVs(n)}>{n}</button>)}</div>
        <div style={head}>Screen</div>
        {DEVICES.map((d) => <button type="button" key={d.id} style={btn(device === d.id)} onClick={() => setDevice(d.id)}>{d.label}</button>)}
        {groups.map((g) => (
          <React.Fragment key={g}>
            <div style={head}>{g}</div>
            {SCENES.filter((x) => x.group === g).map((x) => <button type="button" key={x.id} style={btn(scene === x.id)} onClick={() => { setScene(x.id); setK((v) => v + 1); }}>{x.label}</button>)}
          </React.Fragment>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "grid", placeItems: "center", overflow: "auto" }}>
        <iframe ref={frame} title="Royale" src="/debug/royale-flow?stage=1" style={dev.w ? { width: dev.w, height: dev.h, border: "1px solid rgba(255,255,255,0.25)", borderRadius: 14 } : { width: "100%", height: "100%", border: 0 }} />
      </div>
    </div>
  );
}
